import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import ProjectContext from "@context/Project/projectContext";
import ImageSegmentationViewer from "./ImageSegmentationViewer";
import service from "@services/index";
import { saveAnnotations } from "@services/dashboardService";
import SaveIcon from '@mui/icons-material/Save';
import { showErrorSnackbar } from '@components/shared-layouts/Snackbar/helper';
import {
  parseImageApiAnnotations,
  polygonsToAnnotation,
  mapPolygonsToImageSpace,
  mapPolygonsFromImageSpace,
  resolveCoordRemapMode,
  getPolygonBounds,
  type CoordRemapMode,
  type Polygon,
} from "./helpers";
import {
  fetchProjectLabels,
  labelsFromContextState,
  type ProjectLabel,
} from "../helpers";

interface ImageData {
  id: string;
  url: string;
  width: number;
  height: number;
}

interface SegmentationData {
  [imageId: string]: {
    polygons: Polygon[];
    activePolygon: Point[];
  };
}

interface Point {
  x: number;
  y: number;
}

interface CachedImageEntry {
  imageData: ImageData;
  polygons: Polygon[];
  size: number[];
  targetSize: number[];
  coordRemapMode: CoordRemapMode;
}

const Navbar = styled.div`
  height: 75px;
  background: #f04a23;
  display: flex;
  align-items: center;
  padding: 0 24px;
  box-shadow: 0px 3px 3px -2px rgba(0,0,0,0.2), 
              0px 3px 4px 0px rgba(0,0,0,0.14), 
              0px 1px 8px 0px rgba(0,0,0,0.12);
`;

const NavbarTitle = styled.h1`
  color: white;
  font-size: 20px;
  font-weight: 900;
  margin: 0;
  text-align: center;
  flex: 1;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 75px);
  width: 100%;
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
`;

const NavigationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: #ffffff;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #f04a23;
  color: white;
  border: 2px solid #f04a23;
  border-radius: 4px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #d43d1a;
    border-color: #d43d1a;
  }

  &:disabled {
    background: #cccccc;
    border-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  color: #f04a23;
  border: 2px solid #f04a23;
  border-radius: 4px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f04a23;
    color: white;
  }
`;

const CenterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NavButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid #e0e0e0;
  background: white;
  color: #333;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    border-color: #f04a23;
  }

  &:disabled {
    background: #f5f5f5;
    color: #ccc;
    cursor: not-allowed;
  }
`;

const PageNumber = styled.button<{ isSelected: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${props => props.isSelected ? '#f04a23' : '#e0e0e0'};
  background: ${props => props.isSelected ? '#f04a23' : '#ffffff'};
  color: ${props => props.isSelected ? 'white' : '#333'};
  font-weight: ${props => props.isSelected ? 'bold' : 'normal'};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.isSelected ? '#f04a23' : '#f5f5f5'};
    border-color: #f04a23;
  }
`;

const Ellipsis = styled.span`
  color: #666;
  font-size: 12px;
  padding: 0 4px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const GoToPageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const GoToPageLabel = styled.span`
  font-size: 12px;
  color: #666;
`;

const GoToPageInput = styled.input`
  width: 50px;
  height: 32px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  text-align: center;
  font-size: 12px;
  padding: 0 8px;

  &:focus {
    outline: none;
    border-color: #f04a23;
  }
`;

const GoToPageButton = styled.button`
  padding: 8px 12px;
  background: #f04a23;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #d43d1a;
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  width: 100%;
`;

const ImageViewerContainer = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
`;

const ProgressContainer = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  gap: 10px;
`;

const ProgressWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  max-width: 300px;
  width: 100%;
`;

const ProgressText = styled.div`
  color: #f04a23;
  font-weight: 500;
  font-size: 14px;
`;

const ProgressBarContainer = styled.div`
  width: 300px;
  height: 20px;
  border: 1px solid #ed8623;
  border-radius: 10px;
  overflow: hidden;
  background-color: #fff;
`;

const ProgressBar = styled.div<{ progress: number }>`
  width: ${props => `${props.progress}%`};
  height: 100%;
  background: linear-gradient(to right, #f04a23, #ed8623);
  transition: width 0.3s ease-in-out;
`;

const WelcomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 20px;
  text-align: center;
  color: #666;
`;

const WelcomeIcon = styled.div`
  font-size: 48px;
  color: #f04a23;
  margin-bottom: 20px;
  
  &::before {
    content: '';
    display: block;
    width: 64px;
    height: 64px;
    background: #f04a23;
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z'/%3E%3C/svg%3E") center/contain no-repeat;
  }
`;

const WelcomeTitle = styled.h2`
  color: #f04a23;
  margin-bottom: 10px;
  font-size: 24px;
`;

const WelcomeText = styled.p`
  font-size: 16px;
  max-width: 500px;
  line-height: 1.6;
`;

function SegmentationNewLabellingPage() {
  const { project_type } = useParams<{ project_type: string }>();
  const projectContextInfo = useContext(ProjectContext);
  const { state: projectState } = projectContextInfo;
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [currentImage, setCurrentImage] = useState<ImageData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [goToPageInput, setGoToPageInput] = useState<string>('1');
  
  const [labels, setLabels] = useState<ProjectLabel[]>([]);
  const [segmentationData, setSegmentationData] = useState<SegmentationData>({});
  const [preLoadedAnnotations, setPreLoadedAnnotations] = useState<Polygon[] | null>(null);
  const [imageSizes, setImageSizes] = useState<{ size: number[]; targetSize: number[] } | null>(null);
  const [coordRemapMode, setCoordRemapMode] = useState<CoordRemapMode>('none');
  
  const imageCache = useRef<Map<string, CachedImageEntry>>(new Map());
  const labelsRef = useRef<ProjectLabel[]>([]);

  const selectedImageId = projectState?.imageIds?.[selectedImageIndex] || null;
  const totalImages = projectState?.imageIds?.length || 0;

  const applyCachedEntry = useCallback((entry: CachedImageEntry) => {
    setCurrentImage(entry.imageData);
    setPreLoadedAnnotations(entry.polygons);
    setImageSizes({ size: entry.size, targetSize: entry.targetSize });
    setCoordRemapMode(entry.coordRemapMode);
    setSegmentationData((prev) => ({
      ...prev,
      [entry.imageData.id]: {
        polygons: entry.polygons,
        activePolygon: [],
      },
    }));
  }, []);

  const fetchImageData = useCallback(async (imageId: string) => {
    try {
      setCurrentImage(null);
      setPreLoadedAnnotations(null);
      setImageSizes(null);
      setCoordRemapMode('none');
      setLoading(true);
      setError(null);
      setDownloadProgress(0);

      if (imageCache.current.has(imageId)) {
        const cached = imageCache.current.get(imageId)!;
        applyCachedEntry(cached);
        setDownloadProgress(100);
        setLoading(false);
        return;
      }
      
      const response = await service.get(
        `/api/data/${projectState.projectId}/image/${imageId}`,
        null,
        null,
        'https://genie-be.avawatz.com',
        { 
          onDownloadProgress: (progressEvent: any) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setDownloadProgress(percentCompleted);
          }
        }
      );

      if (!response.data) {
        throw new Error('No data received from the server');
      }

      const size = response.data.size ?? response.data.target_size ?? [512, 512];
      const targetSize = response.data.target_size ?? size;

      const imageData: ImageData = {
        id: imageId,
        url: `data:image/jpeg;base64,${response.data.img_base64}`,
        width: size[0] || 512,
        height: size[1] || 512,
      };

      const rawPolygons = parseImageApiAnnotations(response.data, labelsRef.current);
      const apiBounds = getPolygonBounds(rawPolygons);
      const coordRemapMode = resolveCoordRemapMode(size, targetSize, apiBounds);
      setCoordRemapMode(coordRemapMode);
      const polygons = mapPolygonsToImageSpace(
        rawPolygons,
        size,
        targetSize,
        coordRemapMode
      );

      const cacheEntry: CachedImageEntry = {
        imageData,
        polygons,
        size,
        targetSize,
        coordRemapMode,
      };

      imageCache.current.set(imageId, cacheEntry);

      setDownloadProgress(100);
      setCurrentImage(imageData);
      setPreLoadedAnnotations(polygons);
      setImageSizes({ size, targetSize });
      setSegmentationData((prev) => ({
        ...prev,
        [imageId]: {
          polygons,
          activePolygon: [],
        },
      }));

    } catch (err) {
      console.error('Error fetching image data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load image');
    } finally {
      setLoading(false);
    }
  }, [projectState.projectId, applyCachedEntry]);

  // Load full project labels from API, with project context as fallback
  useEffect(() => {
    if (!projectState?.projectId || projectState.projectId === -1) {
      return;
    }

    const fallbackLabels = labelsFromContextState(
      projectState.labels,
      projectState.colors
    );

    fetchProjectLabels(projectState.projectId, project_type, fallbackLabels).then(
      (loadedLabels) => {
        setLabels(loadedLabels);
        labelsRef.current = loadedLabels;
      }
    );

    // Set first image as default if available and no image is currently selected
    if (projectState?.imageIds && projectState.imageIds.length > 0 && selectedImageIndex === 0 && !currentImage) {
      setSelectedImageIndex(0);
    }
  }, [projectState.projectId, projectState.labels, projectState.colors, projectState.imageIds, project_type, selectedImageIndex, currentImage]);

  // Clear image cache only when leaving the project (not on every image change)
  useEffect(() => {
    const cache = imageCache.current;
    return () => {
      cache.clear();
    };
  }, [projectState?.projectId]);

  // Limit cache size
  const limitCacheSize = useCallback((maxSize: number = 10) => {
    if (imageCache.current.size > maxSize) {
      const firstKey: any = imageCache.current.keys().next().value;
      imageCache.current.delete(firstKey);
    }
  }, []);

  // Load image when selected
  useEffect(() => {
    if (selectedImageId) {
      fetchImageData(selectedImageId);
      limitCacheSize();
    }
  }, [selectedImageId, fetchImageData, limitCacheSize]);

  // Sync segmentation overlays when preloaded annotations change for the current image
  useEffect(() => {
    if (!selectedImageId || preLoadedAnnotations === null) return;

    setSegmentationData((prev) => ({
      ...prev,
      [selectedImageId]: {
        polygons: preLoadedAnnotations,
        activePolygon: [],
      },
    }));
  }, [selectedImageId, preLoadedAnnotations]);

  const saveSegmentation = async () => {
    if (!selectedImageId || !projectState.projectId || !currentImage || !imageSizes) return;

    const currentData = segmentationData[selectedImageId];
    if (!currentData) return;

    const polygonsForApi = mapPolygonsFromImageSpace(
      currentData.polygons,
      imageSizes.size,
      imageSizes.targetSize,
      coordRemapMode
    );

    const annotation = polygonsToAnnotation(
      polygonsForApi,
      labels,
      selectedImageId,
      imageSizes.size,
      imageSizes.targetSize
    );

    try {
      await saveAnnotations({
        project_id: projectState.projectId,
        project_type,
        annotations: [annotation],
      });

      imageCache.current.set(selectedImageId, {
        imageData: currentImage,
        polygons: currentData.polygons,
        size: imageSizes.size,
        targetSize: imageSizes.targetSize,
        coordRemapMode,
      });
    } catch (error) {
      console.error('Error saving segmentation:', error);
      showErrorSnackbar("Error saving segmentations");
    }
  };

  // Navigation functions
  const goToFirstPage = () => {
    if (totalImages > 0) {
      setSelectedImageIndex(0);
      setGoToPageInput('1');
    }
  };

  const goToPreviousPage = () => {
    if (selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
      setGoToPageInput(String(selectedImageIndex));
    }
  };

  const goToNextPage = () => {
    if (selectedImageIndex < totalImages - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
      setGoToPageInput(String(selectedImageIndex + 2));
    }
  };

  const goToLastPage = () => {
    if (totalImages > 0) {
      setSelectedImageIndex(totalImages - 1);
      setGoToPageInput(String(totalImages));
    }
  };

  const handleGoToPage = () => {
    const pageNumber = parseInt(goToPageInput);
    if (pageNumber >= 1 && pageNumber <= totalImages) {
      setSelectedImageIndex(pageNumber - 1);
    }
  };

  const handleGoToPageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGoToPageInput(e.target.value);
  };

  const handleGoToPageKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleGoToPage();
    }
  };

  // Update go to page input when selected image changes
  useEffect(() => {
    setGoToPageInput(String(selectedImageIndex + 1));
  }, [selectedImageIndex]);

  // Export function (placeholder)
  const handleExport = () => {
    console.log('Export functionality to be implemented');
  };

  // Generate page numbers for display
  const getPageNumbers = (): (number | string)[] => {
    if (totalImages <= 7) {
      return Array.from({ length: totalImages }, (_, i) => i + 1);
    }

    const currentPage = selectedImageIndex + 1;
    const pages: (number | string)[] = [];

    if (currentPage <= 4) {
      // Show first 5 pages + ellipsis + last page
      for (let i = 1; i <= 5; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalImages);
    } else if (currentPage >= totalImages - 3) {
      // Show first page + ellipsis + last 5 pages
      pages.push(1);
      pages.push('...');
      for (let i = totalImages - 4; i <= totalImages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page + ellipsis + current-1, current, current+1 + ellipsis + last page
      pages.push(1);
      pages.push('...');
      pages.push(currentPage - 1);
      pages.push(currentPage);
      pages.push(currentPage + 1);
      pages.push('...');
      pages.push(totalImages);
    }

    return pages;
  };

  return (
    <PageContainer>
      <Navbar>
        <NavbarTitle>Image Segmentation Data Labelling</NavbarTitle>
      </Navbar>
      <ContentWrapper>

        <MainContent>
          {loading && (
            <ProgressContainer>
              <ProgressWrapper>
                <ProgressText>Loading image... {downloadProgress}%</ProgressText>
                <ProgressBarContainer>
                  <ProgressBar progress={downloadProgress} />
                </ProgressBarContainer>
              </ProgressWrapper>
            </ProgressContainer>
          )}
          {error && (
            <div style={{ 
              color: 'red',
              padding: '20px',
              textAlign: 'center' 
            }}>
              {error}
            </div>
          )}
          {!loading && !currentImage && !error && (
            <WelcomeContainer>
              <WelcomeIcon />
              <WelcomeTitle>Welcome to Image Segmentation</WelcomeTitle>
              <WelcomeText>
                Images are loading... Please wait.
              </WelcomeText>
            </WelcomeContainer>
          )}
          {!loading && currentImage && imageSizes && (
            <ImageViewerContainer>
              <ImageSegmentationViewer 
                imageData={currentImage}
                projectId={projectState.projectId}
                imageId={selectedImageId ?? ''}
                labelsFromContext={labels}
                preLoadedAnnotations={preLoadedAnnotations}
                segmentationData={segmentationData}
                setSegmentationData={setSegmentationData}
                imageSizes={imageSizes}
                coordRemapMode={coordRemapMode}
              />
            </ImageViewerContainer>
          )}
        </MainContent>
        
        <NavigationContainer>
          <LeftSection>
            <SaveButton onClick={saveSegmentation} disabled={!currentImage || loading}>
              <SaveIcon style={{ fontSize: 16 }} />
              SAVE
            </SaveButton>
            <ExportButton onClick={handleExport}>
              EXPORT
            </ExportButton>
          </LeftSection>
          
          <CenterSection>
            <PaginationContainer>
              <NavButton onClick={goToFirstPage} disabled={selectedImageIndex === 0}>
                K
              </NavButton>
              <NavButton onClick={goToPreviousPage} disabled={selectedImageIndex === 0}>
                &lt;
              </NavButton>
              
              {getPageNumbers().map((page, index) => (
                typeof page === 'string' ? (
                  <Ellipsis key={`ellipsis-${index}`}>...</Ellipsis>
                ) : (
                  <PageNumber
                    key={page}
                    isSelected={selectedImageIndex === page - 1}
                    onClick={() => {
                      setSelectedImageIndex(page - 1);
                      setGoToPageInput(String(page));
                    }}
                  >
                    {page}
                  </PageNumber>
                )
              ))}
              
              <NavButton onClick={goToNextPage} disabled={selectedImageIndex === totalImages - 1}>
                &gt;
              </NavButton>
              <NavButton onClick={goToLastPage} disabled={selectedImageIndex === totalImages - 1}>
                I&gt;
              </NavButton>
            </PaginationContainer>
          </CenterSection>
          
          <RightSection>
            <GoToPageContainer>
              <GoToPageLabel>Go to Page</GoToPageLabel>
              <GoToPageInput
                type="number"
                value={goToPageInput}
                onChange={handleGoToPageInputChange}
                onKeyPress={handleGoToPageKeyPress}
                min="1"
                max={totalImages}
              />
              <GoToPageButton onClick={handleGoToPage}>
                GO TO PAGE NO
              </GoToPageButton>
            </GoToPageContainer>
          </RightSection>
        </NavigationContainer>
      </ContentWrapper>
    </PageContainer>
  );
}

export default SegmentationNewLabellingPage;